import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Stack, useTheme } from "@mui/material";
import { CircleMinus } from "lucide-react";
import type { FormEvent } from "react";
import { type Control, type FieldValues, useForm } from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InstructionFields } from "web_myplann/Portfolio/Forms/ToolsShared/InstructionFields";
import type { InstructionTypeLabels } from "web_myplann/Portfolio/Forms/ToolsShared/useInstructionOptions";
import type { z } from "zod";

type Props<TSchema extends z.ZodTypeAny> = {
	schema: TSchema;
	defaultValues: z.input<TSchema>;
	typeLabels?: InstructionTypeLabels;
	showCount?: boolean;
	onSave: (values: z.input<TSchema>) => void;
	isNew: boolean;
	onCancel: () => void;
	onRemove?: () => void;
};

const PageResources = Resources.Portfolio.AddTools;

export const InstructionForm = <TSchema extends z.ZodTypeAny>({
	schema,
	defaultValues,
	typeLabels,
	showCount,
	onSave,
	isNew,
	onCancel,
	onRemove,
}: Props<TSchema>) => {
	const { t } = useResource();
	const theme = useTheme();

	const form = useForm({
		resolver: zodResolver(schema),
		defaultValues,
		mode: "onChange",
	});

	const isUnchangedEdit = !isNew && !form.formState.isDirty;

	const onSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.stopPropagation();
		void form.handleSubmit(() => {
			onSave(form.getValues());
		})(event);
	};

	return (
		<Stack component="form" onSubmit={onSubmit} pb={theme.spacingTokens.lg}>
			<Stack bgcolor={theme.palette.background.paper}>
				<InstructionFields
					control={form.control as unknown as Control<FieldValues>}
					typeLabels={typeLabels}
					showCount={showCount}
				/>
			</Stack>

			<Stack
				px={theme.spacingTokens.lg}
				pt={theme.spacingTokens.lg}
				gap={theme.spacingTokens.sm}
			>
				<Button type="submit" variant="primary" disabled={isUnchangedEdit}>
					{t(
						isNew
							? PageResources.Actions.addInstruction
							: PageResources.Actions.saveChanges,
					)}
				</Button>
				<Button variant="ghost" onClick={onCancel}>
					{t(PageResources.Actions.cancel)}
				</Button>
				{onRemove && (
					<Stack alignItems="center" py={theme.spacingTokens.sm}>
						<Button
							variant="textLink"
							color="error"
							startIcon={<CircleMinus size={theme.iconSize.sm} />}
							onClick={onRemove}
						>
							{t(PageResources.Actions.removeInstruction)}
						</Button>
					</Stack>
				)}
			</Stack>
		</Stack>
	);
};
